import {Form, Input, Select, Switch} from 'antd';
import React from "react";
import {IFieldOptionsEditorProps} from "../FieldOptionsEditor";
import DirectoryPicker from "../../../../../shared/pickers/file/DirectoryPicker";
import {IFilePickerWidgetType} from "../../../../../shared/pickers/file/FilePicker";
import LocaleText from "../../../../settings/dictionary/LocaleText";

export interface IFieldFileOptions {
    fileWidget?: IFilePickerWidgetType
    fileCanChoose?: boolean
    fileCanUpload?: boolean
    fileCanDownload?: boolean
    fileCanRemove?: boolean
    fileMultiple?: boolean
    fileDisabled?: boolean
    fileDirectory?: number
    fileExtensions?: string[]
    fileModalPicker?: boolean
}

const EXTENSION_OPTIONS = [
    // --- GLOBAL BUNDLES ---
    { label: 'All Files (No Filter)', value: '*' },
    { label: 'All Images', value: 'image/*' },
    { label: 'All Video', value: 'video/*' },
    { label: 'All Audio', value: 'audio/*' },

    // --- DOCUMENTS (Office & Productivity) ---
    { label: 'PDF Document', value: 'application/pdf,.pdf' },
    { label: 'Word (Modern) .docx', value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx' },
    { label: 'Word (Legacy) .doc', value: 'application/msword,.doc' },
    { label: 'Excel (Modern) .xlsx', value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx' },
    { label: 'Excel (Legacy) .xls', value: 'application/vnd.ms-excel,.xls' },
    { label: 'Excel Binary .xlsb', value: 'application/vnd.ms-excel.sheet.binary.macroEnabled.12,.xlsb' },
    { label: 'PowerPoint (Modern) .pptx', value: 'application/vnd.openxmlformats-officedocument.presentationml.presentation,.pptx' },
    { label: 'PowerPoint (Legacy) .ppt', value: 'application/vnd.ms-powerpoint,.ppt' },
    { label: 'OpenDocument Text .odt', value: 'application/vnd.oasis.opendocument.text,.odt' },
    { label: 'OpenDocument Spreadsheet .ods', value: 'application/vnd.oasis.opendocument.spreadsheet,.ods' },
    { label: 'Rich Text .rtf', value: 'application/rtf,text/rtf,.rtf' },
    { label: 'Plain Text .txt', value: 'text/plain,.txt' },
    { label: 'CSV Data .csv', value: 'text/csv,application/vnd.ms-excel,text/x-csv,text/comma-separated-values,.csv' },

    // --- IMAGES (Web, Print & Pro) ---
    { label: 'JPEG Image', value: 'image/jpeg,.jpg,.jpeg' },
    { label: 'PNG Image', value: 'image/png,.png' },
    { label: 'GIF Animation', value: 'image/gif,.gif' },
    { label: 'WebP Next-Gen', value: 'image/webp,.webp' },
    { label: 'SVG Vector', value: 'image/svg+xml,.svg' },
    { label: 'TIFF (High Res)', value: 'image/tiff,image/x-tiff,.tif,.tiff' },
    { label: 'Adobe Photoshop .psd', value: 'image/vnd.adobe.photoshop,application/x-photoshop,.psd' },
    { label: 'Adobe Illustrator .ai', value: 'application/postscript,application/pdf,.ai' },
    { label: 'Adobe InDesign .indd', value: 'application/x-indesign,.indd' },
    { label: 'CorelDRAW .cdr', value: 'application/coreldraw,application/x-cdr,.cdr' },
    { label: 'Icon File .ico', value: 'image/x-icon,image/vnd.microsoft.icon,.ico' },
    { label: 'HEIC (Apple Photos)', value: 'image/heic,image/heif,.heic,.heif' },
    { label: 'RAW Camera Image', value: 'image/x-canon-cr2,image/x-nikon-nef,image/x-adobe-dng,.raw,.cr2,.nef,.dng' },
    { label: 'PostScript .eps', value: 'application/postscript,image/x-eps,.eps' },

    // --- WEB & SOFTWARE DEVELOPMENT (Code) ---
    { label: 'HTML Webpage', value: 'text/html,.html,.htm' },
    { label: 'CSS Stylesheet', value: 'text/css,.css' },
    { label: 'JavaScript .js', value: 'text/javascript,application/javascript,.js' },
    { label: 'TypeScript .ts', value: 'text/typescript,video/mp2t,.ts,.tsx' },
    { label: 'JSON Data', value: 'application/json,text/plain,.json' },
    { label: 'XML Data', value: 'application/xml,text/xml,.xml' },
    { label: 'PHP Script', value: 'application/x-httpd-php,text/x-php,.php' },
    { label: 'Python Script .py', value: 'text/x-python,application/x-python-code,.py' },
    { label: 'Java Source .java', value: 'text/x-java-source,text/plain,.java' },
    { label: 'C++ Source .cpp', value: 'text/x-c,text/plain,.cpp,.cxx,.cc' },
    { label: 'C# Source .cs', value: 'text/plain,.cs' },
    { label: 'Ruby Script .rb', value: 'text/x-ruby,application/x-ruby,.rb' },
    { label: 'Markdown .md', value: 'text/markdown,text/plain,.md,.markdown' },
    { label: 'YAML Config', value: 'text/yaml,application/x-yaml,text/plain,.yaml,.yml' },
    { label: 'SQL Database Script', value: 'text/x-sql,application/sql,text/plain,.sql' },
    { label: 'Shell Script .sh', value: 'application/x-sh,text/x-shellscript,.sh' },
    { label: 'DockerFile', value: 'text/plain,.dockerfile' },
    { label: 'Log File .log', value: 'text/plain,.log' },

    // --- ARCHIVES & INSTALLERS ---
    { label: 'ZIP Archive', value: 'application/zip,application/x-zip-compressed,application/x-compress,multipart/x-zip,.zip' },
    { label: 'RAR Archive', value: 'application/vnd.rar,application/x-rar-compressed,.rar' },
    { label: '7-Zip Archive', value: 'application/x-7z-compressed,.7z' },
    { label: 'Tarball .tar.gz', value: 'application/x-gtar,application/x-tar,application/gzip,.tar,.gz,.tgz' },
    { label: 'Gzip .gz', value: 'application/gzip,application/x-gzip,.gz' },
    { label: 'Windows Installer .msi', value: 'application/x-msi,application/x-msdownload,.msi' },
    { label: 'Executable .exe', value: 'application/x-msdownload,application/x-ms-dos-executable,.exe' },
    { label: 'Apple Disk Image .dmg', value: 'application/x-apple-diskimage,.dmg' },
    { label: 'ISO Disk Image', value: 'application/x-iso9660-image,.iso' },

    // --- VIDEO PRODUCTION ---
    { label: 'MP4 Video', value: 'video/mp4,.mp4' },
    { label: 'QuickTime Movie .mov', value: 'video/quicktime,.mov' },
    { label: 'AVI Video', value: 'video/x-msvideo,.avi' },
    { label: 'Matroska .mkv', value: 'video/x-matroska,.mkv' },
    { label: 'WebM Video', value: 'video/webm,.webm' },
    { label: 'WMV (Windows Media)', value: 'video/x-ms-wmv,.wmv' },
    { label: 'Flash Video .flv', value: 'video/x-flv,.flv' },
    { label: 'Subtitle File .srt', value: 'text/plain,application/x-subrip,.srt' },

    // --- AUDIO & MUSIC ---
    { label: 'MP3 Audio', value: 'audio/mpeg,audio/mp3,.mp3' },
    { label: 'WAV (Lossless)', value: 'audio/wav,audio/x-wav,.wav' },
    { label: 'FLAC (Lossless)', value: 'audio/flac,audio/x-flac,.flac' },
    { label: 'AAC Audio', value: 'audio/aac,audio/aacp,.aac' },
    { label: 'MIDI Music', value: 'audio/midi,audio/x-midi,.mid,.midi' },
    { label: 'M4A (Apple Audio)', value: 'audio/m4a,audio/x-m4a,audio/mp4,.m4a' },
    { label: 'OGG Audio', value: 'audio/ogg,application/ogg,.ogg' },

    // --- ENGINEERING & SCIENTIFIC ---
    { label: 'AutoCAD .dwg', value: 'image/vnd.dwg,application/acad,.dwg' },
    { label: 'AutoCAD DXF .dxf', value: 'image/vnd.dxf,application/dxf,.dxf' },
    { label: '3D Step File .step', value: 'application/step,text/plain,.step,.stp' },
    { label: 'STL (3D Print)', value: 'model/stl,application/sla,.stl' },
    { label: 'SketchUp .skp', value: 'application/x-koan,.skp' },
    { label: 'Revit .rvt', value: 'application/octet-stream,.rvt' },
    { label: 'Google Earth .kml', value: 'application/vnd.google-earth.kml+xml,.kml' },
    { label: 'GPX (GPS Exchange)', value: 'application/gpx+xml,text/xml,.gpx' },
    { label: 'DICOM (Medical)', value: 'application/dicom,.dcm,.dicom' },
    { label: 'SQLite DB', value: 'application/x-sqlite3,application/vnd.sqlite3,.sqlite,.db' },

    // --- FONTS ---
    { label: 'TrueType Font .ttf', value: 'font/ttf,application/x-font-ttf,.ttf' },
    { label: 'OpenType Font .otf', value: 'font/otf,application/x-font-opentype,.otf' },
    { label: 'Web Font .woff', value: 'font/woff,application/font-woff,.woff' },
    { label: 'Web Font .woff2', value: 'font/woff2,.woff2' },

    // --- MISC & EBOOKS ---
    { label: 'EPUB Ebook', value: 'application/epub+zip,.epub' },
    { label: 'Kindle .mobi', value: 'application/x-mobipocket-ebook,.mobi' },
    { label: 'VCard (Contact)', value: 'text/vcard,text/x-vcard,.vcf' },
    { label: 'Calendar .ics', value: 'text/calendar,.ics' },
    { label: 'Certificate .crt', value: 'application/x-x509-ca-cert,application/pkix-cert,.crt,.cer' },
    { label: 'Binary Data .bin', value: 'application/octet-stream,.bin' }
];

interface IProps extends IFieldOptionsEditorProps {

}

interface IState {
}

class FieldFileOptionsEditor extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.state = {}
    }

    static getDefaultOptions(): IFieldFileOptions {
        return {
            fileCanChoose: true,
            fileCanDownload: false,
            fileCanRemove: false,
            fileCanUpload: true,
            fileWidget: 'simple'
        }
    }

    render() {
        const {buildFieldName} = this.props

        const types: { code: IFilePickerWidgetType, label: string }[] = [
            {code: "simple", label: 'Jednoduchý'},
            {code: "extended", label: 'Rozšířený'}
        ]

        const switches = [
            {label: 'Nahrání', code: 'fileCanUpload', initialValue: true},
            {label: 'Prohlížeč', code: 'fileCanChoose', initialValue: false},
            {label: 'Stahování', code: 'fileCanDownload', initialValue: false},
            {label: 'Odstranění', code: 'fileCanRemove', initialValue: false},
            {label: 'Nahrání modal', code: 'fileModalPicker', initialValue: false}
        ]

        return (
            <div>
                <Form.Item name={buildFieldName('fileWidget')} initialValue={'simple'}
                           label={'Vyberte režim výběru souborů'} rules={[{required: true}]}>
                    <Select>
                        {types.map(type => (
                            <Select.Option key={type.code} value={type.code}>{type.label}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item label={'Možnosti'}>
                    <Input.Group>
                        {switches.map(switchItem => (
                            <Form.Item name={buildFieldName(switchItem.code)} initialValue={switchItem.initialValue}
                                       valuePropName={'checked'} noStyle>
                                <Switch className={'mr-2'}
                                        unCheckedChildren={switchItem.label}
                                        checkedChildren={switchItem.label}
                                />
                            </Form.Item>
                        ))}
                    </Input.Group>
                </Form.Item>
                <Form.Item name={buildFieldName('fileExtensions')} label={<LocaleText code={'configuration.field.file.extensions'} fallback={'Povolené typy souborů'}/>}>
                    <Select
                        showSearch
                        placeholder="Search 100+ formats..."
                        mode={'multiple'}
                        optionFilterProp="label" // Crucial: allows searching by the label text
                        style={{ width: '100%' }}
                        options={EXTENSION_OPTIONS}
                    />
                </Form.Item>
                <Form.Item name={buildFieldName('fileDirectory')} label={'Omezit na jednu složku'}>
                    <DirectoryPicker defaultRoot={false}/>
                </Form.Item>
            </div>
        )
    }
}


export default FieldFileOptionsEditor